import { getBaseUrl, getWebBaseUrl } from '../config/appSettings';
import { pick, toIntPercent, unwrapData, unwrapList } from './moduleHelpers';

/** Public URL for admin-uploaded RewardPicture in C:\Partner Rewards. */
export const resolveRewardPictureUrl = (raw) => {
  const value = String(raw || '').trim();
  if (!value || value === 'null' || value === 'undefined') return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
    return value;
  }
  const base = (getWebBaseUrl() || getBaseUrl() || '').replace(/\/$/, '');
  const path = value.replace(/\\/g, '/').replace(/^\//, '');
  const fileName = path.split('/').pop() || '';
  const isLegacy = /^(documents|Uploads)\//i.test(path);
  if (!isLegacy && fileName && !path.includes('/')) {
    return `${base}/${encodeURIComponent('Partner Rewards')}/${fileName}`;
  }
  return base ? `${base}/${path}` : `/${path}`;
};

const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const nested = (payload) => payload?.result ?? payload?.data ?? payload ?? {};

/** Normalize GetRewardList rows to a stable shape. */
export const normalizeRewardList = (response) => {
  const list = unwrapList(response);
  return list.map((item, index) => {
    const stepNumber = index + 1;
    const requiredLots = toNum(
      item.rewardRequiredPoints ??
        item.RewardRequiredPoints ??
        item.requiredPoints ??
        item.RequiredPoints ??
        item.requiredLots ??
        item.RequiredLots ??
        item.requiredLot ??
        item.lotsRequired ??
        item.pointsCost ??
        item.points
    );
    const requiredActiveClients = toNum(
      item.requiredActiveClients ??
        item.RequiredActiveClients ??
        item.requiredClients ??
        item.activeClientsRequired
    );
    return {
      ...item,
      id: item.affiliateRewardId ?? item.rewardId ?? item.id ?? stepNumber,
      affiliateRewardId: item.affiliateRewardId ?? item.rewardId ?? item.id,
      rewardName: item.rewardName || item.name || `Reward ${stepNumber}`,
      description: item.description || item.rewardDescription || item.details || '',
      requiredLots,
      requiredActiveClients,
      pointsCost: requiredLots,
      requiredPoints: requiredLots,
      rewardValue: toNum(item.rewardValue ?? item.RewardValue ?? item.value),
      stepNumber,
      category: item.category || item.rewardCategory || item.type || item.rewardTypeName || 'Milestone',
      isPopular: Boolean(item.isPopular || item.mostPopular || item.IsPopular),
      imageUrl: resolveRewardPictureUrl(
        pick(
          item,
          'imageUrl',
          'ImageUrl',
          'rewardPicture',
          'RewardPicture',
          'fileUrl',
          'FileUrl',
          'image',
          'iconUrl'
        )
      ),
    };
  });
};

/** Normalize GET /Rewards/GetMilestoneProgress (TotalLoyaltyPoints eligibility). */
export const parseMilestoneProgress = (payload) => {
  let data = payload;
  for (let i = 0; i < 3; i += 1) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) break;
    const nested = data.result ?? data.Result ?? data.data ?? data.Data;
    if (
      nested &&
      typeof nested === 'object' &&
      !Array.isArray(nested) &&
      (nested.milestones != null ||
        nested.Milestones != null ||
        nested.totalPointsEarned != null ||
        nested.TotalPointsEarned != null ||
        nested.stats != null ||
        nested.Stats != null)
    ) {
      data = nested;
      continue;
    }
    break;
  }
  data = data && typeof data === 'object' ? data : {};

  const stats = data.stats || data.Stats || {};
  const milestonesRaw = data.milestones || data.Milestones || [];

  const readNum = (...vals) => {
    for (const v of vals) {
      if (v == null || v === '') continue;
      const n = typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };

  const availablePoints = readNum(
    data.availableLoyaltyPoints,
    data.AvailableLoyaltyPoints,
    data.availablePoints,
    data.AvailablePoints
  );
  const totalPointsEarned = readNum(
    data.totalPointsEarned,
    data.TotalPointsEarned,
    data.totalLoyaltyPoints,
    data.TotalLoyaltyPoints
  );

  const mapStatus = (isClaimed, requiredPts, availablePts) => {
    if (isClaimed) return { state: 'claimed', badge: 'Claimed', status: 'CLAIMED' };
    // AVAILABLE when spendable balance covers required points
    if (availablePts >= requiredPts && requiredPts >= 0) {
      return { state: 'current', badge: 'Available', status: 'AVAILABLE' };
    }
    return { state: 'locked', badge: 'Locked', status: 'LOCKED' };
  };

  const milestones = (Array.isArray(milestonesRaw) ? milestonesRaw : [])
    .map((m, i) => {
      const isClaimed = Boolean(m.isClaimed ?? m.IsClaimed);
      const required = readNum(m.requiredPoints, m.RequiredPoints, m.requiredLots, m.points);
      const statusInfo = mapStatus(isClaimed, required, availablePoints);
      const isEligible = availablePoints >= required;
      return {
        id: m.affiliateRewardId ?? m.AffiliateRewardId ?? m.id ?? i,
        affiliateRewardId: m.affiliateRewardId ?? m.AffiliateRewardId,
        sequenceNo: readNum(m.sequenceNo, m.SequenceNo) || i + 1,
        name: m.rewardName || m.RewardName || m.name || `Milestone ${i + 1}`,
        rewardName: m.rewardName || m.RewardName || m.name,
        points: required,
        requiredLots: required,
        requiredPoints: required,
        rewardValue: readNum(m.rewardValue, m.RewardValue),
        rewardPicture: resolveRewardPictureUrl(m.rewardPicture || m.RewardPicture),
        imageUrl: resolveRewardPictureUrl(m.rewardPicture || m.RewardPicture || m.imageUrl),
        isClaimed,
        isEligible,
        status: statusInfo.status,
        badge: statusInfo.badge,
        state: statusInfo.state,
      };
    })
    .sort((a, b) => a.sequenceNo - b.sequenceNo || a.requiredPoints - b.requiredPoints);

  const claimedCount = milestones.filter((m) => m.state === 'claimed' || m.isClaimed).length;
  const availableCount = milestones.filter((m) => m.state === 'current' || m.status === 'AVAILABLE').length;

  // Prefer API stats, but milestonesReached must mean CLAIMED count.
  const totalMilestones =
    readNum(stats.totalMilestones, stats.TotalMilestones) || milestones.length;
  const rewardsAvailable =
    readNum(stats.rewardsAvailable, stats.RewardsAvailable) || availableCount;
  const pendingRewards = readNum(stats.pendingRewards, stats.PendingRewards);
  const totalValueUnlocked = readNum(stats.totalValueUnlocked, stats.TotalValueUnlocked);

  // Next reward = first non-claimed milestone (AVAILABLE preferred, else next LOCKED).
  const nextMilestone =
    milestones.find((m) => m.status === 'AVAILABLE' || m.state === 'current') ||
    milestones.find((m) => !m.isClaimed && m.state !== 'claimed') ||
    null;

  const nextRequired = nextMilestone ? nextMilestone.requiredPoints : 0;
  // Available logic: AvailableLoyaltyPoints >= required
  const pointsNeeded = nextRequired > 0 ? Math.max(0, nextRequired - availablePoints) : 0;
  const progressPercent =
    nextRequired > 0 ? toIntPercent(availablePoints, nextRequired) : nextMilestone ? 0 : 100;

  return {
    availablePoints,
    availableLoyaltyPoints: availablePoints,
    pointsToSpend: availablePoints,
    currentPoints: availablePoints,
    totalPointsEarned,
    totalLoyaltyPoints: totalPointsEarned,
    nextReward: nextMilestone,
    nextRewardName: nextMilestone?.rewardName || nextMilestone?.name || 'Max milestone reached',
    nextRewardTarget: nextRequired,
    nextRewardCurrent: Math.min(availablePoints, nextRequired || availablePoints),
    nextRewardProgress: Math.round(progressPercent),
    pointsNeeded,
    nextRewardStatus: nextMilestone?.status || '',
    stats,
    milestonesReached: claimedCount,
    totalMilestones,
    rewardsAvailable,
    pendingCount: pendingRewards,
    pendingRewards,
    claimedCount,
    totalValueUnlocked,
    milestones,
  };
};

/** Normalize GET /Rewards/GetRewardsDashboard (AffiliateWallet points + milestones). */
export const parseRewardsDashboard = (payload) => {
  let data = payload;
  // Accept raw envelope or nested result/data wrappers.
  for (let i = 0; i < 3; i += 1) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) break;
    const nested = data.result ?? data.Result ?? data.data ?? data.Data;
    if (
      nested &&
      typeof nested === 'object' &&
      !Array.isArray(nested) &&
      (nested.milestones != null ||
        nested.Milestones != null ||
        nested.availablePoints != null ||
        nested.AvailablePoints != null ||
        nested.totalPointsEarned != null ||
        nested.TotalPointsEarned != null ||
        nested.yourTier != null ||
        nested.YourTier != null)
    ) {
      data = nested;
      continue;
    }
    break;
  }
  data = data && typeof data === 'object' ? data : {};

  const yourTier = data.yourTier || data.YourTier || {};
  const nextReward = data.nextReward || data.NextReward || {};
  const stats = data.stats || data.Stats || {};
  const milestonesRaw = data.milestones || data.Milestones || [];

  const readNum = (...vals) => {
    for (const v of vals) {
      if (v == null || v === '') continue;
      const n = typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };

  const availablePoints = readNum(
    data.availablePoints,
    data.AvailablePoints,
    data.availableLoyaltyPoints,
    data.AvailableLoyaltyPoints,
    data.pointsToSpend,
    data.PointsToSpend
  );
  const totalPointsEarned = readNum(
    data.totalPointsEarned,
    data.TotalPointsEarned,
    data.totalLoyaltyPoints,
    data.TotalLoyaltyPoints,
    data.lifetimePoints,
    data.LifetimePoints
  );

  const mapStatus = (status, isClaimed, isAchieved) => {
    const s = String(status || '').toUpperCase();
    if (isClaimed || s === 'CLAIMED') return { state: 'claimed', badge: 'Claimed' };
    if (s === 'PENDING') return { state: 'pending', badge: 'Pending' };
    if (s === 'AVAILABLE') return { state: 'current', badge: 'Available' };
    if (s === 'LOCKED') return { state: 'locked', badge: 'Locked' };
    if (isAchieved && !isClaimed) return { state: 'current', badge: 'Available' };
    return { state: 'locked', badge: 'Locked' };
  };

  const milestones = (Array.isArray(milestonesRaw) ? milestonesRaw : []).map((m, i) => {
    const statusInfo = mapStatus(m.status ?? m.Status, m.isClaimed ?? m.IsClaimed, m.isAchieved ?? m.IsAchieved);
    const required = readNum(m.requiredPoints, m.RequiredPoints, m.requiredLots, m.points);
    return {
      id: m.affiliateRewardId ?? m.AffiliateRewardId ?? m.id ?? i,
      affiliateRewardId: m.affiliateRewardId ?? m.AffiliateRewardId,
      sequenceNo: readNum(m.sequenceNo, m.SequenceNo) || i + 1,
      name: m.rewardName || m.RewardName || m.name || `Milestone ${i + 1}`,
      rewardName: m.rewardName || m.RewardName || m.name,
      points: required,
      requiredLots: required,
      requiredPoints: required,
      rewardPicture: resolveRewardPictureUrl(m.rewardPicture || m.RewardPicture),
      imageUrl: resolveRewardPictureUrl(m.rewardPicture || m.RewardPicture || m.imageUrl || m.ImageUrl),
      isClaimed: Boolean(m.isClaimed ?? m.IsClaimed),
      isAchieved: Boolean(m.isAchieved ?? m.IsAchieved),
      status: String(m.status || m.Status || statusInfo.badge).toUpperCase(),
      badge: statusInfo.badge,
      state: statusInfo.state,
    };
  });

  const nextRequired = readNum(nextReward.requiredPoints, nextReward.RequiredPoints);
  const pointsNeeded = readNum(nextReward.pointsNeeded, nextReward.PointsNeeded);
  const progressPercent = readNum(
    nextReward.progressPercent,
    nextReward.ProgressPercent,
    toIntPercent(totalPointsEarned, nextRequired || 1)
  );

  const totalMilestones = readNum(stats.totalMilestones, stats.TotalMilestones) || milestones.length;
  const rewardsAvailable = readNum(stats.rewardsAvailable, stats.RewardsAvailable);
  const pendingRewards = readNum(stats.pendingRewards, stats.PendingRewards);
  const totalValueUnlocked = readNum(stats.totalValueUnlocked, stats.TotalValueUnlocked);
  const claimedCount = milestones.filter((m) => m.state === 'claimed' || m.isClaimed).length;

  return {
    availablePoints,
    availableLoyaltyPoints: availablePoints,
    pointsToSpend: availablePoints,
    currentPoints: availablePoints,
    totalPointsEarned,
    totalLoyaltyPoints: totalPointsEarned,
    yourTier,
    rewardTierName: pick(yourTier, 'tierName', 'TierName') || '—',
    tierName: pick(yourTier, 'tierName', 'TierName') || '—',
    rewardTierRequiredPoints: readNum(yourTier.requiredPoints, yourTier.RequiredPoints),
    rewardCurrentLevel: readNum(yourTier.affiliateRewardTierId, yourTier.AffiliateRewardTierId),
    nextRewardTierName: pick(yourTier, 'nextTierName', 'NextTierName') || '',
    nextRewardTierPoints: readNum(yourTier.nextTierRequiredPoints, yourTier.NextTierRequiredPoints),
    pointsToNextTier: readNum(yourTier.pointsToNextTier, yourTier.PointsToNextTier),
    nextReward,
    nextRewardName: pick(nextReward, 'rewardName', 'RewardName') || 'Max tier reached',
    nextRewardTarget: nextRequired,
    nextRewardCurrent: Math.max(0, nextRequired - pointsNeeded),
    nextRewardProgress: Math.round(progressPercent),
    pointsNeeded,
    nextRewardStatus: pick(nextReward, 'status', 'Status') || '',
    stats,
    // Always count claimed milestones (API "reached" can include available/unlocked).
    milestonesReached: claimedCount,
    totalMilestones,
    rewardsAvailable,
    pendingCount: pendingRewards,
    pendingRewards,
    claimedCount,
    totalValueUnlocked,
    milestones,
  };
};

/** Normalize GetClaimRewardHistoryAll rows. */
export const normalizeClaimHistory = (response) => {
  const list = unwrapList(response);
  return list.map((item, index) => {
    const statusText = String(item.status || item.rewardStatus || item.Status || '').toLowerCase();
    const rewardName = item.rewardName || item.RewardName || item.name || item.Name || '';
    // API may only return { rewardName, isClaimed }.
    const explicitlyPending =
      statusText.includes('pending') || item.isClaimed === false || item.IsClaimed === false;
    const claimed =
      item.isClaimed === true ||
      item.IsClaimed === true ||
      (!explicitlyPending &&
        (statusText.includes('claim') ||
          statusText.includes('redeem') ||
          Boolean(item.claimedDate) ||
          Boolean(rewardName)));

    const pointsCost = toNum(
      item.pointsDeducted ??
        item.PointsDeducted ??
        item.rewardRequiredPoints ??
        item.RewardRequiredPoints ??
        item.requiredPoints ??
        item.RequiredPoints ??
        item.pointsCost ??
        item.PointsCost ??
        item.pointsUsed ??
        item.PointsUsed ??
        item.loyaltyPoints ??
        item.LoyaltyPoints ??
        item.points ??
        item.Points ??
        item.requiredLots
    );
    return {
      ...item,
      id:
        item.affiliateRewardAchieveId ??
        item.AffiliateRewardAchieveId ??
        item.affiliateRewardAchievedHistoryId ??
        item.id ??
        item.affiliateRewardId ??
        item.AffiliateRewardId ??
        item.rewardId ??
        index,
      affiliateRewardId:
        item.affiliateRewardId ??
        item.AffiliateRewardId ??
        item.rewardId ??
        item.RewardId ??
        item.reward?.affiliateRewardId ??
        null,
      rewardName: rewardName || `Reward #${index + 1}`,
      name: rewardName || `Reward #${index + 1}`,
      description: item.description || item.rewardDescription || '',
      status: claimed ? 'Redeemed' : 'Pending',
      isClaimed: claimed,
      createdDateTime:
        item.createdDateTime ||
        item.CreatedDateTime ||
        item.createdAt ||
        item.CreatedAt ||
        item.claimedDate ||
        item.date ||
        item.claimDate ||
        '',
      claimedDate:
        item.claimedDate ||
        item.CreatedDateTime ||
        item.createdDateTime ||
        item.date ||
        item.createdAt ||
        item.claimDate ||
        '',
      pointsCost,
      pointsDeducted: pointsCost,
      requiredPoints: pointsCost,
    };
  });
};

/** Fill missing claim points from the rewards catalog by affiliateRewardId. */
export const enrichClaimHistoryWithRewards = (claimed = [], rewards = []) => {
  if (!Array.isArray(claimed) || !claimed.length) return claimed || [];
  if (!Array.isArray(rewards) || !rewards.length) return claimed;

  const byId = new Map();
  rewards.forEach((r) => {
    const id = r.affiliateRewardId ?? r.id;
    if (id != null) byId.set(Number(id), r);
  });

  return claimed.map((item) => {
    if (Number(item.pointsCost) > 0) return item;
    const match = byId.get(Number(item.affiliateRewardId ?? item.rewardId ?? item.id));
    if (!match) return item;
    const pts = toNum(
      match.requiredPoints ?? match.pointsCost ?? match.requiredLots ?? match.rewardRequiredPoints
    );
    if (!(pts > 0)) return item;
    return {
      ...item,
      pointsCost: pts,
      pointsDeducted: pts,
      requiredPoints: pts,
      rewardName: item.rewardName || match.rewardName || match.name,
    };
  });
};

export const extractRewardProgress = (widgetsRes, commissionRes) => {
  const widgets = nested(unwrapData(widgetsRes) ?? widgetsRes);
  const commission = nested(unwrapData(commissionRes) ?? commissionRes);

  const rewardCurrentLevel = toNum(
    pick(
      widgets,
      'rewardCurrentLevel',
      'RewardCurrentLevel',
      'currentRewardLevel',
      'CurrentRewardLevel',
      'rewardLevel',
      'affiliateRewardTierId',
      'AffiliateRewardTierId'
    )
  );

  const totalLots = toNum(
    pick(commission, 'totalLots', 'TotalLots') ??
      pick(widgets, 'totalLots', 'TotalLots', 'totalTradingVolume', 'TotalTradingVolume')
  );

  const totalActiveClients = toNum(
    pick(commission, 'totalActiveClients', 'TotalActiveClients', 'activeClients') ??
      pick(widgets, 'totalActiveClients', 'TotalActiveClients', 'activeClients', 'ActiveClients')
  );

  const rewardPoints = toNum(
    pick(
      widgets,
      'rewardPoints',
      'RewardPoints',
      'rewardTierPoints',
      'RewardTierPoints',
      'availablePoints',
      'totalPoints'
    ) ?? totalLots
  );

  return { rewardCurrentLevel, totalLots, totalActiveClients, rewardPoints, widgets, commission };
};

/** Normalize GetRewardTierList / GetRewardTierPointsList rows. */
export const normalizeRewardTierList = (response) => {
  const list = unwrapList(response);
  return list
    .map((item, index) => ({
      affiliateRewardTierId: toNum(
        item.affiliateRewardTierId ?? item.AffiliateRewardTierId ?? item.id ?? index + 1
      ),
      affiliateRewardTierPointsId: toNum(
        item.affiliateRewardTierPointsId ?? item.AffiliateRewardTierPointsId ?? 0
      ),
      rewardTierName: String(
        item.rewardTierName || item.RewardTierName || item.tierName || item.name || `Tier ${index + 1}`
      ),
      rewardTierPoints: toNum(item.rewardTierPoints ?? item.RewardTierPoints ?? item.points ?? 0),
      isActive: item.isActive !== false && item.IsActive !== false,
    }))
    .filter((t) => t.isActive)
    .sort((a, b) => a.rewardTierPoints - b.rewardTierPoints || a.affiliateRewardTierId - b.affiliateRewardTierId);
};

/**
 * Resolve current reward tier from tier list.
 * Prefers widget level id match; otherwise highest tier unlocked by points.
 */
export const resolveCurrentRewardTier = (tiers = [], progress = {}) => {
  const list = Array.isArray(tiers) ? tiers : [];
  if (!list.length) return null;

  const levelId = toNum(progress.rewardCurrentLevel);
  if (levelId > 0) {
    const byId = list.find((t) => t.affiliateRewardTierId === levelId);
    if (byId) return byId;
  }

  const points = toNum(progress.rewardPoints ?? progress.totalLots);
  let current = list[0];
  list.forEach((tier) => {
    if (points >= tier.rewardTierPoints) current = tier;
  });
  return current;
};

export const resolveNextRewardTier = (tiers = [], currentTier) => {
  const list = Array.isArray(tiers) ? tiers : [];
  if (!list.length || !currentTier) return null;
  return (
    list.find((t) => t.rewardTierPoints > currentTier.rewardTierPoints) ||
    list.find((t) => t.affiliateRewardTierId > currentTier.affiliateRewardTierId) ||
    null
  );
};

export const buildRewardsSummary = ({ rewards, claimed, progress, rewardTiers = [] }) => {
  const { rewardCurrentLevel, totalLots, totalActiveClients, rewardPoints } = progress;
  const availableLoyaltyPoints = toNum(
    progress.availableLoyaltyPoints ?? progress.availablePoints ?? rewardPoints ?? totalLots
  );
  const totalLoyaltyPoints = toNum(
    progress.totalLoyaltyPoints ?? progress.totalPointsEarned ?? availableLoyaltyPoints
  );
  // Progress toward milestones uses lifetime points (not spendable balance).
  const pointsForProgress = totalLoyaltyPoints;

  const claimedIds = new Set(
    claimed
      .filter((c) => c.isClaimed)
      .map((c) => String(c.affiliateRewardId ?? c.rewardId ?? c.id ?? ''))
  );

  const isClaimedReward = (reward) =>
    claimedIds.has(String(reward.affiliateRewardId ?? reward.id)) || reward.isClaimed === true;

  const levelIndex = Math.max(0, rewardCurrentLevel - 1);
  const currentReward = rewards[levelIndex] || rewards.find((r) => r.stepNumber === rewardCurrentLevel);

  // Next unlock target = first unclaimed reward; fall back to level-based next.
  const nextReward =
    rewards.find((r) => !isClaimedReward(r) && pointsForProgress < toNum(r.requiredLots)) ||
    rewards.find((r) => !isClaimedReward(r)) ||
    rewards.find((r) => r.stepNumber === rewardCurrentLevel + 1) ||
    rewards.find((r) => r.stepNumber > rewardCurrentLevel) ||
    null;

  const claimedCount = claimed.filter((c) => c.isClaimed).length;
  const pendingCount = claimed.filter((c) => !c.isClaimed).length;
  const availableCount = rewards.filter((r) => !isClaimedReward(r) && pointsForProgress >= toNum(r.requiredLots)).length;

  const nextTargetLots = toNum(nextReward?.requiredLots || currentReward?.requiredLots || 0);
  const nextProgress = toIntPercent(pointsForProgress, nextTargetLots || 1);

  const currentTier = resolveCurrentRewardTier(rewardTiers, {
    ...progress,
    rewardPoints: pointsForProgress,
  });
  const nextTier = resolveNextRewardTier(rewardTiers, currentTier);
  const nextTierProgress = nextTier
    ? toIntPercent(pointsForProgress, nextTier.rewardTierPoints || 1)
    : 100;

  const tierName = currentTier?.rewardTierName || '—';
  const tierRequired = currentTier?.rewardTierPoints ?? 0;

  return {
    availablePoints: availableLoyaltyPoints,
    availableLoyaltyPoints,
    totalPointsEarned: totalLoyaltyPoints,
    totalLoyaltyPoints,
    pointsToSpend: availableLoyaltyPoints,
    currentPoints: availableLoyaltyPoints,
    totalLots,
    totalActiveClients,
    rewardCurrentLevel: currentTier?.affiliateRewardTierId || rewardCurrentLevel,
    rewardTierName: tierName,
    rewardTierRequiredPoints: tierRequired,
    tierName,
    rewardTiers,
    currentRewardTier: currentTier,
    nextRewardTier: nextTier,
    nextRewardTierName: nextTier?.rewardTierName || 'Max tier reached',
    nextRewardTierPoints: nextTier?.rewardTierPoints ?? 0,
    nextRewardTierProgress: nextTierProgress,
    currentMilestoneName: currentReward?.rewardName || null,
    nextRewardName: nextReward?.rewardName || 'Max tier reached',
    nextRewardTarget: nextTargetLots,
    nextRewardCurrent: pointsForProgress,
    nextRewardProgress: nextProgress,
    rewardsAvailable: availableCount,
    claimedCount,
    pendingCount,
    currentReward,
    nextReward,
  };
};

export const buildMilestones = (rewards, progress, claimed) => {
  const { rewardCurrentLevel, totalLots, totalActiveClients } = progress;
  const pointsForProgress = toNum(
    progress.totalLoyaltyPoints ?? progress.rewardPoints ?? totalLots
  );
  const claimedIds = new Set(
    claimed
      .filter((c) => c.isClaimed !== false)
      .map((c) => String(c.affiliateRewardId ?? c.rewardId ?? ''))
      .filter((id) => id && id !== 'null' && id !== 'undefined')
  );
  const claimedNames = new Set(
    claimed
      .filter((c) => c.isClaimed !== false)
      .map((c) => String(c.rewardName || c.name || '').trim().toLowerCase())
      .filter(Boolean)
  );

  return rewards.map((reward) => {
    const required = toNum(reward.requiredLots);
    const unlocked =
      rewardCurrentLevel >= reward.stepNumber || (required > 0 && pointsForProgress >= required);
    const rewardNameKey = String(reward.rewardName || reward.name || '')
      .trim()
      .toLowerCase();
    const alreadyClaimed =
      claimedIds.has(String(reward.affiliateRewardId ?? reward.id)) ||
      (rewardNameKey && claimedNames.has(rewardNameKey)) ||
      reward.isClaimed === true;
    let state = 'locked';
    let badge = 'Locked';
    if (alreadyClaimed) {
      state = 'claimed';
      badge = 'Claimed';
    } else if (unlocked) {
      state = 'current';
      badge = 'Available';
    } else if (reward.stepNumber === rewardCurrentLevel + 1) {
      state = 'current';
      badge = 'In progress';
    }

    const lotsPct = toIntPercent(pointsForProgress, required || 1);
    const clientsPct = toIntPercent(totalActiveClients, reward.requiredActiveClients || 1);

    return {
      id: reward.affiliateRewardId ?? reward.id,
      name: reward.rewardName,
      points: reward.requiredLots,
      requiredLots: reward.requiredLots,
      requiredActiveClients: reward.requiredActiveClients,
      lotsProgress: lotsPct,
      clientsProgress: clientsPct,
      currentLots: pointsForProgress,
      currentClients: totalActiveClients,
      badge,
      state,
      stepNumber: reward.stepNumber,
      description: reward.description,
      unlocked,
      alreadyClaimed,
    };
  });
};

export const enrichRewardsForClaim = (rewards, progress, claimed) => {
  const { totalLots, totalActiveClients } = progress;
  // Spendable wallet balance — claim unlock must use this, not lots/level gates.
  const availableWalletPoints = toNum(
    progress.availableLoyaltyPoints ?? progress.availablePoints ?? progress.pointsToSpend
  );
  const lifetimePoints = toNum(
    progress.totalLoyaltyPoints ?? progress.totalPointsEarned ?? progress.rewardPoints ?? totalLots
  );

  const claimedList = Array.isArray(claimed) ? claimed : [];
  // GetClaimRewardHistoryAll currently returns RewardName + IsClaimed (no reward id).
  const claimedIds = new Set(
    claimedList
      .filter((c) => c.isClaimed !== false)
      .map((c) => String(c.affiliateRewardId ?? c.rewardId ?? ''))
      .filter((id) => id && id !== 'null' && id !== 'undefined')
  );
  const claimedNames = new Set(
    claimedList
      .filter((c) => c.isClaimed !== false)
      .map((c) => String(c.rewardName || c.name || '').trim().toLowerCase())
      .filter(Boolean)
  );

  return rewards.map((reward) => {
    const rewardKey = String(reward.affiliateRewardId ?? reward.id ?? '');
    const rewardNameKey = String(reward.rewardName || reward.name || '')
      .trim()
      .toLowerCase();
    const alreadyClaimed =
      claimedIds.has(rewardKey) ||
      (rewardNameKey && claimedNames.has(rewardNameKey)) ||
      reward.isClaimed === true ||
      reward.claimed === true ||
      reward.alreadyClaimed === true ||
      String(reward.status || '').toLowerCase() === 'claimed';
    const required = toNum(
      reward.requiredPoints ?? reward.pointsCost ?? reward.requiredLots ?? reward.points
    );
    const canAfford = required <= 0 || availableWalletPoints >= required;
    const pointsShortfall = Math.max(0, required - availableWalletPoints);
    const available = !alreadyClaimed && canAfford;

    return {
      ...reward,
      requiredPoints: required,
      pointsCost: required,
      requiredLots: required,
      isClaimed: alreadyClaimed,
      claimed: alreadyClaimed,
      alreadyClaimed,
      isAvailable: available,
      canAfford,
      pointsShortfall,
      availableWalletPoints,
      status: alreadyClaimed ? 'claimed' : available ? 'available' : 'locked',
      lifetimePoints,
      totalLots,
      totalActiveClients,
    };
  });
};
